import { StatusCodes } from 'http-status-codes';
import type { HttpClient } from 'services/api-client/client';
import { getResponseErrorStatus, returnData } from 'services/api-client/client';
import type { AccountApi } from '@env0/account-service/api';
import type { CreateVcsProviderUserMapping, UpdateVcsProviderUserMapping } from 'types/api.types';

export default (client: HttpClient) => ({
  getVcsProviderUserMappings: () => {
    return client.get<AccountApi.VcsProviderUserMapping[]>(`users/me/vcs-provider-user-mappings`).then(returnData);
  },
  createVcsProviderUserMapping: (payload: CreateVcsProviderUserMapping) => {
    return client
      .post<AccountApi.VcsProviderUserMapping>(`users/me/vcs-provider-user-mappings`, payload)
      .then(returnData);
  },
  updateVcsProviderUserMapping: (payload: UpdateVcsProviderUserMapping) => {
    return client
      .put<AccountApi.VcsProviderUserMapping>(`vcs-provider-user-mappings/${payload.id}`, payload)
      .then(returnData);
  },
  connectVerifiedVcsProviderUserMapping: (payload: AccountApi.ConnectVerifiedVcsProviderUserMapping.Request.Body) => {
    return client
      .post<AccountApi.ConnectVerifiedVcsProviderUserMapping.Response>(
        `users/me/vcs-provider-user-mappings/verified`,
        payload
      )
      .then(returnData);
  },
  getScimConfiguration: (organizationId: string) => {
    return client
      .get<AccountApi.Scim.GetScimConfiguration.Response>(`scim/configuration/${organizationId}`, {
        hideNotification: true
      })
      .then(returnData);
  },
  createScimConfiguration: (organizationId: string) => {
    return client
      .post<AccountApi.Scim.CreateScimConfiguration.Response>(`scim/configuration/${organizationId}`)
      .then(returnData);
  },
  deleteScimConfiguration: (organizationId: string) => {
    return client.delete(`scim/configuration/${organizationId}`);
  },
  getScimGroupMappings: (organizationId: string) => {
    return client
      .get<AccountApi.Scim.GetScimGroupMappings.Response>(`scim/group-team-mapping/${organizationId}`)
      .then(returnData);
  },
  deleteScimGroupMapping: (organizationId: string, mappingId: string) => {
    return client.delete(`scim/group-team-mapping/${organizationId}/${mappingId}`);
  },
  rotateScimConfiguration: (organizationId: string) => {
    return client
      .post<AccountApi.Scim.RotateScimToken.Response>(`scim/configuration/${organizationId}/rotate-token`)
      .then(returnData);
  },
  updateScimMode: (organizationId: string, mode: AccountApi.Scim.UpdateScimMode.Request.Body['mode']) => {
    return client
      .put<AccountApi.Scim.UpdateScimMode.Response>(`scim/configuration/${organizationId}/mode`, { mode })
      .then(returnData);
  },
  reconcileScim: (organizationId: string, { dryRun, confirm }: { dryRun: boolean; confirm?: boolean }) => {
    return client
      .post<AccountApi.Scim.ReconcileScim.Response>(`scim/reconcile/${organizationId}`, undefined, {
        hideNotification: true,
        params: { dryRun: String(dryRun), ...(confirm ? { confirm: 'true' } : {}) }
      })
      .then(returnData);
  },
  getScimReconcileJob: (organizationId: string) => {
    // an org that never ran an apply has no job, and the endpoint answers 404 — that is a normal
    // state, not something to raise a sticky error toast on every SCIM settings visit. Every other
    // failure is real and is rethrown, for the reconcile panel to surface inline.
    return client
      .get<AccountApi.Scim.GetScimReconcileJob.Response>(`scim/reconcile/${organizationId}/job`, {
        hideNotification: true
      })
      .then(returnData)
      .catch(error => {
        if (getResponseErrorStatus(error) === StatusCodes.NOT_FOUND) {
          return null;
        }

        throw error;
      });
  },
  getScimJitSamlConfig: (organizationId: string) => {
    return client
      .get<AccountApi.Scim.GetJitSamlConfig.Response>(`scim/configuration/${organizationId}/jit-saml-config`)
      .then(returnData);
  }
});
